import { FilterBar } from '@/components/filters/FilterBar';
import { SearchInput } from '@/components/filters/SearchInput';
import { Badge } from '@/components/ui/Badge';
import { DataTable } from '@/components/ui/DataTable';
import { Stat } from '@/components/ui/Stat';
import { useSupportStats, useSupportTickets } from '@/hooks/useAdminSupport';
import type {
  AgentEtat,
  SupportTicket,
} from '@/services/admin-support.service';
import { formatNumber, formatRelativeTime } from '@/utils/formatters';
import { createColumnHelper } from '@tanstack/react-table';
import {
  AlertCircle,
  Bot,
  CheckCircle,
  Clock,
  Inbox,
  Mail,
  MessageSquare,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const statusLabels: Record<string, string> = {
  open: 'Ouvert',
  in_progress: 'En cours',
  waiting_for_user: 'En attente du client',
  resolved: 'Résolu',
  closed: 'Fermé',
};

const statusVariants: Record<
  string,
  'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'brand'
> = {
  open: 'danger',
  in_progress: 'warning',
  waiting_for_user: 'info',
  resolved: 'success',
  closed: 'neutral',
};

/**
 * Ce que l'assistant du support a fait de la demande : une pastille de ligne,
 * pas une colonne ni un écran. « Remonté » dit pourquoi.
 */
export function AgentBadge({
  ticket,
}: {
  ticket: Pick<SupportTicket, 'agentEtat' | 'agentMotif'>;
}) {
  if (ticket.agentEtat === 'repondu') {
    return (
      <Badge variant="brand" title="L’assistant du support a répondu ; une copie a été envoyée à l’équipe.">
        <Bot className="mr-1 h-3 w-3" aria-hidden="true" />
        Répondu par l’assistant
      </Badge>
    );
  }
  if (ticket.agentEtat === 'remonte') {
    return (
      <Badge
        variant="warning"
        title={ticket.agentMotif ?? 'L’assistant du support a remonté cette demande.'}
      >
        <Bot className="mr-1 h-3 w-3" aria-hidden="true" />
        Remonté{ticket.agentMotif ? ` : ${ticket.agentMotif}` : ''}
      </Badge>
    );
  }
  return null;
}

const priorityLabels: Record<string, string> = {
  low: 'Basse',
  normal: 'Normale',
  high: 'Haute',
  urgent: 'Urgente',
};

const priorityVariants: Record<
  string,
  'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'brand'
> = {
  low: 'neutral',
  normal: 'info',
  high: 'warning',
  urgent: 'danger',
};

const columnHelper = createColumnHelper<SupportTicket>();

const columns = [
  columnHelper.accessor('subject', {
    header: 'Sujet',
    cell: (info) => (
      <div>
        <p className="font-medium text-neutral-900 line-clamp-1 dark:text-neutral-200">
          {info.getValue()}
        </p>
        <p className="flex items-center gap-1 text-xs text-neutral-500">
          {info.row.original.origin === 'email' && (
            <Mail className="h-3 w-3" aria-label="Reçu par e-mail" />
          )}
          {info.row.original.user?.email ||
            info.row.original.requesterEmail ||
            '-'}
        </p>
        <AgentBadge ticket={info.row.original} />
      </div>
    ),
  }),
  columnHelper.accessor('status', {
    header: 'Statut',
    cell: (info) => (
      <Badge variant={statusVariants[info.getValue()] || 'neutral'}>
        {statusLabels[info.getValue()] || info.getValue()}
      </Badge>
    ),
  }),
  columnHelper.accessor('priority', {
    header: 'Priorite',
    cell: (info) => (
      <Badge variant={priorityVariants[info.getValue()] || 'neutral'}>
        {priorityLabels[info.getValue()] || info.getValue()}
      </Badge>
    ),
  }),
  columnHelper.accessor('replyCount', {
    header: 'Reponses',
    cell: (info) => (
      <div className="flex items-center gap-1 text-neutral-600 dark:text-neutral-400">
        <MessageSquare className="h-3.5 w-3.5" />
        <span>{info.getValue()}</span>
      </div>
    ),
  }),
  columnHelper.accessor('user', {
    header: 'Utilisateur',
    cell: (info) => {
      const user = info.getValue();
      if (user) {
        return (
          <span className="text-sm text-neutral-600 dark:text-neutral-400">
            {user.firstName} {user.lastName}
          </span>
        );
      }
      const externe = info.row.original.requesterName;
      return externe ? (
        <span className="text-sm text-neutral-600 dark:text-neutral-400">
          {externe} <span className="text-xs text-neutral-400">(sans compte)</span>
        </span>
      ) : (
        '-'
      );
    },
  }),
  columnHelper.accessor('createdAt', {
    header: 'Cree',
    cell: (info) => (
      <span className="text-xs text-neutral-500">
        {formatRelativeTime(info.getValue())}
      </span>
    ),
  }),
];

export function SupportTicketsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState<AgentEtat | ''>('');
  const navigate = useNavigate();

  const { data: stats } = useSupportStats();
  const { data, isLoading } = useSupportTickets({
    search: search || undefined,
    status: statusFilter || undefined,
    agent: agentFilter || undefined,
    limit: 25,
  });
  const tickets = data?.data || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
          Support
        </h1>
        <p className="text-sm text-neutral-500">
          Gestion des tickets de support utilisateurs
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Stat
          label="Total"
          value={formatNumber(stats?.total)}
          icon={<Inbox className="h-5 w-5" />}
        />
        <Stat
          label="Ouverts"
          value={formatNumber(stats?.open)}
          icon={<AlertCircle className="h-5 w-5" />}
        />
        <Stat
          label="En cours"
          value={formatNumber(stats?.inProgress)}
          icon={<Clock className="h-5 w-5" />}
        />
        <Stat
          label="Resolus"
          value={formatNumber(stats?.resolved)}
          icon={<CheckCircle className="h-5 w-5" />}
        />
        <Stat
          label="Temps moyen"
          value={
            stats?.avgResponseTimeMinutes != null
              ? `${stats.avgResponseTimeMinutes} min`
              : '--'
          }
          icon={<Clock className="h-5 w-5" />}
        />
      </div>

      <FilterBar>
        <div className="w-72">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Rechercher un ticket..."
          />
        </div>
        <div className="flex gap-2">
          {(
            [
              '',
              'open',
              'in_progress',
              'waiting_for_user',
              'resolved',
              'closed',
            ] as const
          ).map(
            (s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`rounded-lg px-3 py-1.5 text-xs transition-colors ${
                  statusFilter === s
                    ? 'bg-primary-500 text-white'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700'
                }`}
              >
                {s === '' ? 'Tous' : statusLabels[s]}
              </button>
            ),
          )}
        </div>
        <div className="flex gap-2" aria-label="Filtrer selon l’assistant du support">
          {(
            [
              ['', 'Toutes'],
              ['repondu', 'Répondues par l’assistant'],
              ['remonte', 'Remontées par l’assistant'],
            ] as const
          ).map(([valeur, libelle]) => (
            <button
              key={valeur}
              onClick={() => setAgentFilter(valeur)}
              className={`rounded-lg px-3 py-1.5 text-xs transition-colors ${
                agentFilter === valeur
                  ? 'bg-primary-500 text-white'
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700'
              }`}
            >
              {libelle}
            </button>
          ))}
        </div>
      </FilterBar>

      <DataTable
        columns={columns}
        data={tickets}
        loading={isLoading}
        onRowClick={(row) => navigate(`/support/${row.uuid}`)}
        emptyMessage="Aucun ticket trouve"
      />
    </div>
  );
}
